import { z } from 'zod';

/**
 * Recursos do domínio como a API os entrega — o mesmo formato de `@escala/domain/types`
 * (o teste de tipos em `resources.test.ts` trava a equivalência). Cada schema com `id`
 * vira um componente da documentação OpenAPI.
 */

/** O `MAX_NICKNAME` de `@escala/domain/members`, repetido (travado pelo `contracts.test.ts`). */
export const NICKNAME_MAX = 40;

export const IdSchema = z.string().min(1).max(64);

/** Data local `yyyy-mm-dd`. */
export const IsoDateSchema = z.iso.date();

/** Hora local `HH:mm`. */
export const TimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora no formato HH:mm');
/** Cor `#RRGGBB` (da paleta ou livre). */
export const HexColorSchema = z.string().regex(/^#[0-9A-F]{6}$/i);

export const AppRoleSchema = z.enum(['admin', 'leader', 'roleLeader', 'member']).meta({
  id: 'AppRole',
  description:
    'Perfil de acesso: Administrador, Líder de Ministério, Líder de Função ou Membro. Só Administrador e Membro se ' +
    'escolhem; os perfis de líder vêm das lideranças (`leaderIds` dos ministérios e das funções).',
});

export const ThemeSchema = z.enum(['light', 'dark']).meta({
  id: 'Theme',
  description: 'Tema da interface: claro ou escuro.',
});

export const SexSchema = z.enum(['M', 'F']).meta({ id: 'Sex' });

export const RoleSexSchema = z
  .enum(['any', 'M', 'F'])
  .meta({ id: 'RoleSex', description: 'Sexo permitido na função (`any` = qualquer).' });

export const AvailStateSchema = z
  .enum(['available', 'unavailable', 'unset'])
  .meta({ id: 'AvailState', description: '`unset` = o membro ainda não disse se pode no período.' });

export const MinistryIconKeySchema = z
  .enum([
    'door',
    'users',
    'home',
    'heart',
    'music',
    'mic',
    'speaker',
    'monitor',
    'video',
    'camera',
    'msg',
    'smile',
    'star',
    'book',
    'flame',
    'cross',
    'globe',
    'gift',
    'coffee',
    'utensils',
    'shield',
    'car',
    'palette',
    'health',
    'calendar',
    'guitar',
    'drum',
    'headphones',
    'radio',
    'film',
    'image',
    'smartphone',
    'megaphone',
    'church',
    'bell',
    'handshake',
    'helping',
    'baby',
    'blocks',
    'graduation',
    'coins',
    'clipboard',
    'broom',
    'lightbulb',
    'sparkles',
  ])
  .meta({ id: 'MinistryIconKey' });

export const RoleIconKeySchema = z
  .enum([
    'user',
    'users',
    'coordinator',
    'leader',
    'mic',
    'choir',
    'guitar',
    'bass',
    'keyboard',
    'piano',
    'drums',
    'percussion',
    'mixer',
    'speaker',
    'audio',
    'headset',
    'camera',
    'cameraOperator',
    'gimbal',
    'video',
    'director',
    'live',
    'projector',
    'slides',
    'lights',
    'mediaDesk',
    'videoEditor',
    'photoEditor',
    'social',
    'story',
    'reels',
    'designer',
    'art',
    'copy',
    'planning',
    'content',
    'opening',
    'closing',
    'welcome',
    'word',
    'preaching',
    'offering',
    'announcements',
    'monitor',
    'prayer',
    'intercession',
    'door',
    'guard',
    'parking',
    'support',
    'cleaning',
    'organization',
    'teacher',
    'dynamics',
    'nursery',
    'kids',
    'coffee',
    'kitchen',
    'firstAid',
    'communion',
    'baptism',
    'decoration',
    'interpreter',
    'accessibility',
    'star',
  ])
  .meta({ id: 'RoleIconKey', description: 'Ícone da função (cada um com a sua cor).' });

export const CoupleModeSchema = z.enum(['together', 'alternate']).meta({
  id: 'CoupleMode',
  description: '`together` = servir juntos quando possível; `alternate` = revezar os cultos (um por culto).',
});

/** Regras avançadas da função (Ministérios › "Regras avançadas"). */
export const RoleRulesShape = {
  critical: z
    .boolean()
    .meta({ description: 'Função crítica: a geração a preenche antes das demais do evento.' }),
  minExperience: z
    .number()
    .int()
    .min(0)
    .max(50)
    .meta({ description: 'Escalas anteriores nesta função exigidas (0 = sem exigência).' }),
  minYears: z
    .number()
    .int()
    .min(0)
    .max(50)
    .meta({ description: 'Anos de membresia exigidos, pela data "Membro desde" (0 = sem exigência).' }),
  trainees: z.array(IdSchema).max(100).meta({
    description: 'Em treinamento: só entram com alguém experiente na função e ficam isentos da experiência.',
  }),
  blockedPairs: z
    .array(z.tuple([IdSchema, IdSchema]))
    .max(100)
    .meta({ description: 'Pares que não servem juntos: com um nesta função, o outro não entra no evento.' }),
  togetherPairs: z
    .array(z.tuple([IdSchema, IdSchema]))
    .max(100)
    .meta({
      description:
        'Pares que servem juntos: com um nesta função, a geração escala o outro na mesma função do evento, se puder (preferência).',
    }),
};

export const RoleSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1).max(80),
    qty: z.number().int().min(1).max(20).meta({ description: 'Quantidade padrão por evento.' }),
    allowedSex: RoleSexSchema,
    rules: z.array(z.string().min(1).max(120)).max(30),
    leaderIds: z
      .array(IdSchema)
      .max(20)
      .meta({ description: 'Líderes de Função (nenhum, um ou mais), entre os habilitados nela.' }),
    icon: RoleIconKeySchema,
    ...RoleRulesShape,
  })
  .meta({ id: 'Role', description: 'Função de um ministério.' });

export const MinistrySchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1).max(80),
    description: z.string().max(500),
    color: z.string().regex(/^#[0-9A-F]{6}$/, 'Cor em hexadecimal (#RRGGBB)'),
    icon: MinistryIconKeySchema,
    leaderIds: z.array(IdSchema).max(20).meta({ description: 'Líderes de Ministério (nenhum, um ou mais).' }),
    roles: z.array(RoleSchema),
    welcome: z.boolean().meta({
      description: 'Ministério de acolhimento: os líderes dele gerenciam as fichas de visitantes.',
    }),
  })
  .meta({ id: 'Ministry' });

export const BlackoutSchema = z
  .object({
    id: IdSchema,
    from: IsoDateSchema,
    to: IsoDateSchema,
    reason: z.string().max(200),
  })
  .meta({ id: 'Blackout', description: 'Ausência programada (de/até, inclusive).' });

const PhoneDigitsSchema = z.union([z.literal(''), z.string().regex(/^[1-9][1-9]([2-8]\d{7}|9\d{8})$/)]);

/** Os dígitos verificadores e a repetição são conferidos pelo servidor (`memberCpfError`). */
const CpfDigitsSchema = z.union([z.literal(''), z.string().regex(/^\d{11}$/)]);

export const CivilStatusSchema = z
  .enum(['single', 'married', 'stableUnion', 'divorced', 'widowed'])
  .meta({ id: 'CivilStatus', description: 'Estado civil.' });

export const ChurchStatusSchema = z.enum(['active', 'congregant', 'away', 'transferring']).meta({
  id: 'ChurchStatus',
  description: 'Situação na igreja: Membro em comunhão, Congregado, Afastado ou Em transferência.',
});

export const JoinedViaSchema = z
  .enum(['baptism', 'profession', 'transfer', 'acclamation', 'reconciliation'])
  .meta({ id: 'JoinedVia', description: 'Forma de ingresso como membro.' });

export const AddressSchema = z
  .object({
    cep: z.union([z.literal(''), z.string().regex(/^\d{8}$/)]).meta({ description: 'Só os 8 dígitos.' }),
    street: z.string().max(160),
    number: z.string().max(20),
    complement: z.string().max(80),
    district: z.string().max(80),
    city: z.string().max(80),
    uf: z.union([z.literal(''), z.string().regex(/^[A-Z]{2}$/)]),
  })
  .meta({ id: 'Address', description: 'Endereço; `""` = não informado.' });

export const EmergencyContactSchema = z
  .object({ name: z.string().max(120), relation: z.string().max(40), phone: PhoneDigitsSchema })
  .meta({ id: 'EmergencyContact' });

/** Tipos da foto do membro (`PHOTO_TYPES` de `@escala/domain/photo`). */
export const PhotoTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']).meta({ id: 'PhotoType' });

/** O base64 da foto já reduzida: `MAX_PHOTO_BASE64` de `@escala/domain/photo` (64 KB). */
export const PHOTO_BASE64_MAX = 87_384;

/** A foto em `data:` URL (modo demonstração) cabe com folga no endereço. */
const PHOTO_URL_MAX = PHOTO_BASE64_MAX + 64;

export const GeoPrecisionSchema = z.enum(['address', 'street', 'district', 'city']).meta({
  id: 'GeoPrecision',
  description: 'Até onde a coordenada é precisa: o número, a rua, o bairro ou só a cidade.',
});

export const MemberLocationSchema = z
  .object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    precision: GeoPrecisionSchema,
    addressKey: z
      .string()
      .max(400)
      .meta({ description: 'O endereço de onde a coordenada veio (`addressKey`).' }),
    at: z.number().int().meta({ description: 'Quando foi obtida (epoch ms).' }),
  })
  .meta({ id: 'MemberLocation', description: 'Coordenadas do endereço do membro.' });

export const MemberSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1).max(120),
    nickname: z.string().max(NICKNAME_MAX).meta({
      description: 'Nome de apresentação (o do crachá, o apelido). `""` quando não informado.',
    }),
    email: z
      .union([z.literal(''), z.email().max(254)])
      .meta({ description: 'E-mail do membro, que é também o do login. `""` quando não informado.' }),
    phone: PhoneDigitsSchema.meta({
      description: 'Telefone (WhatsApp): só os dígitos, com DDD e sem o +55. `""` quando não informado.',
    }),
    birthDate: IsoDateSchema.nullable(),
    sex: SexSchema,
    photo: z
      .string()
      .max(PHOTO_URL_MAX)
      .nullable()
      .meta({
        description:
          'Endereço da foto (o avatar), `/api/v1/members/{id}/photo/{token}` — o token muda a cada envio, e a ' +
          'imagem é servida sem login, com cache longo. `null` = sem foto (a interface mostra as iniciais). Muda só ' +
          'por `PUT`/`DELETE /members/{id}/photo`.',
      }),
    spouseId: IdSchema.nullable(),
    spouseText: z.string().max(120),
    coupleMode: CoupleModeSchema.nullable().meta({
      description:
        'Escala do casal, a mesma nos dois cônjuges; `null` sem preferência ou sem cônjuge vinculado.',
    }),
    roles: z
      .record(IdSchema, z.array(IdSchema))
      .meta({ description: 'ministryId → roleIds em que o membro está habilitado.' }),
    availability: z
      .array(AvailStateSchema)
      .length(21)
      .meta({ description: '21 posições: `weekday * 3 + period` (domingo = 0; manhã, tarde, noite).' }),
    blackouts: z.array(BlackoutSchema),
    civilStatus: CivilStatusSchema.nullable(),
    profession: z.string().max(80),
    churchStatus: ChurchStatusSchema.nullable(),
    memberSince: IsoDateSchema.nullable(),
    joinedVia: JoinedViaSchema.nullable(),
    baptismDate: IsoDateSchema.nullable(),
    originChurch: z.string().max(120).meta({ description: 'Igreja anterior (transferência ou aclamação).' }),
    cpf: CpfDigitsSchema.meta({
      description:
        'CPF: só os 11 dígitos, `""` quando não informado. Opcional, válido (dígitos verificadores) e único na ' +
        'organização (409 `member_cpf`). Só o Administrador e o próprio membro veem e alteram; os demais ' +
        'recebem em branco, e o `PUT` deles mantém o gravado.',
    }),
    address: AddressSchema.meta({
      description: 'Só o Administrador e o próprio membro veem; os demais recebem tudo em branco.',
    }),
    emergency: EmergencyContactSchema.meta({
      description: 'Só o Administrador e o próprio membro veem; os demais recebem tudo em branco.',
    }),
    location: MemberLocationSchema.nullable().meta({
      description:
        'Coordenadas obtidas do endereço pelo servidor (OpenStreetMap/Nominatim), na fila de geocodificação; ' +
        '`null` enquanto não localizado. Privadas como o endereço (os demais recebem `null`); o `PUT` não as ' +
        'altera — endereço novo apaga a antiga e pede outra busca.',
    }),
    inactiveSince: IsoDateSchema.nullable().meta({
      description:
        'Situação nas escalas: `null` é ativo (pode ser escalado, com ou sem acesso); a data é desde quando está ' +
        'inativo — fora das escalas de hoje em diante, da geração e do seletor. Muda só por ' +
        '`POST /members/{id}/deactivate` e `/reactivate`; o `PUT` mantém o valor gravado.',
    }),
    selfUpdatedAt: z.number().int().nullable().meta({
      description:
        'Quando a própria pessoa atualizou o cadastro pelo link de completar (ms); `null` = nunca. O `PUT` mantém.',
    }),
    admin: z.boolean().meta({
      description:
        'Administrador da organização (escolhido em Acessos, `PATCH /accesses/{memberId}`). Os perfis de líder não ' +
        'vêm daqui: saem das lideranças. O `PUT` mantém o valor gravado.',
    }),
  })
  .meta({ id: 'Member' });

export const ScheduleNoticeSchema = z
  .object({
    kind: z.enum(['removed', 'moved', 'added']).meta({
      description: 'Saiu (`roleId` = de onde saiu), mudou de função (a nova) ou voltou (onde voltou).',
    }),
    roleId: IdSchema,
  })
  .meta({ id: 'ScheduleNotice', description: 'Mudança na escala publicada, para avisar só quem mudou.' });

export const SwapStatusSchema = z
  .enum(['peer', 'leader', 'approved', 'rejected', 'declined', 'canceled'])
  .meta({
    id: 'SwapStatus',
    description:
      'Aguardando o colega (`peer`), aguardando o líder (`leader`), aprovada, recusada pelo líder, recusada pelo colega ou cancelada (a escala mudou ou quem pediu desistiu).',
  });

export const SwapRequestSchema = z
  .object({
    id: IdSchema,
    roleId: IdSchema.meta({ description: 'A função de quem pediu, que o colega assume.' }),
    fromId: IdSchema,
    toId: IdSchema,
    status: SwapStatusSchema,
    at: z.number().int().meta({ description: 'Última mudança da situação (epoch ms).' }),
  })
  .meta({
    id: 'SwapRequest',
    description: 'Pedido de troca: quem pediu, o colega que assume e a situação.',
  });

export const ScheduleResponseSchema = z
  .object({
    status: z.enum(['confirmed', 'declined']),
    roleId: IdSchema.meta({ description: 'Função em que estava escalado (a recusa reabre a vaga nela).' }),
    reason: z.string().max(200).meta({ description: 'Motivo da recusa (`""` na confirmação).' }),
    at: z.number().int().meta({ description: 'Momento em milissegundos (epoch).' }),
  })
  .meta({ id: 'ScheduleResponse', description: 'Confirmação ou recusa do membro escalado.' });

export const AttendanceSchema = z
  .object({
    status: z.enum(['present', 'absent']),
    substituteId: IdSchema.nullable().meta({ description: 'Na falta: quem serviu no lugar.' }),
  })
  .meta({ id: 'Attendance', description: 'Check-in do dia: compareceu ou faltou.' });

export const RecurrenceEndSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('never') }),
    z.object({ kind: z.literal('until'), date: IsoDateSchema }),
    z.object({ kind: z.literal('count'), count: z.number().int().min(1).max(104) }),
  ])
  .meta({
    id: 'RecurrenceEnd',
    description:
      'Quando a repetição termina: nunca (cria as datas até o fim do ano da primeira), numa data ou depois de N vezes.',
  });

export const SkippedDateSchema = z
  .object({
    date: IsoDateSchema,
    time: TimeSchema.meta({ description: 'O horário que a data tinha.' }),
    reason: z.string().max(80).meta({ description: 'O motivo ("Feriado"); vazio sem motivo.' }),
    notified: z
      .array(IdSchema)
      .meta({ description: 'Quem estava na escala publicada e recebe o aviso do cancelamento.' }),
    at: z.number().int().meta({ description: 'Quando foi excluída (ms).' }),
  })
  .meta({
    id: 'SkippedDate',
    description: 'Uma data excluída sozinha da série (`@escala/domain/skippedDates`).',
  });

export const RecurrenceRuleSchema = z
  .object({
    freq: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
    interval: z
      .number()
      .int()
      .min(1)
      .max(99)
      .meta({ description: 'A cada quantos dias, semanas, meses ou anos.' }),
    start: IsoDateSchema.meta({ description: 'A primeira data da regra; o intervalo conta a partir dela.' }),
    weekdays: z
      .array(z.number().int().min(0).max(6))
      .max(7)
      .meta({ description: 'Semanal: os dias da semana (0 = domingo). Nas outras, vazio.' }),
    monthly: z
      .discriminatedUnion('kind', [
        z.object({ kind: z.literal('day'), day: z.number().int().min(1).max(31) }),
        z.object({
          kind: z.literal('weekday'),
          nth: z.number().int().min(-1).max(5),
          weekday: z.number().int().min(0).max(6),
        }),
      ])
      .nullable()
      .meta({ description: 'Mensal: no dia do mês ou no n-ésimo dia da semana (`nth` -1 = o último).' }),
    yearly: z
      .object({ month: z.number().int().min(1).max(12), day: z.number().int().min(1).max(31) })
      .nullable()
      .meta({ description: 'Anual: o mês e o dia.' }),
    end: RecurrenceEndSchema,
    skipped: z.array(SkippedDateSchema).max(500).optional().meta({
      description: 'As datas excluídas sozinhas ("Só esta data"): não voltam ao estender nem no passado.',
    }),
  })
  .meta({
    id: 'RecurrenceRule',
    description:
      'Repetição do evento (`@escala/domain/recurrence`). O servidor confere com `recurrenceError` (intervalo, ' +
      'dias, fim e no máximo 104 datas de uma vez).',
  });

export const AudienceMethodSchema = z.enum(['manual', 'estimate', 'counter', 'other']).meta({
  id: 'AudienceMethod',
  description: 'Como o público foi contado: contagem manual, estimativa, contador na entrada ou outro.',
});

const AudienceCountSchema = z.number().int().min(0).max(100_000);

/** As contagens do público do culto, como o formulário as envia. */
export const AudienceInputShape = {
  total: AudienceCountSchema.meta({ description: 'Público presencial total, crianças incluídas.' }),
  members: AudienceCountSchema.nullable().meta({ description: 'Membros presentes (contagem, sem nomes).' }),
  firstTime: AudienceCountSchema.nullable().meta({ description: 'Visitantes de primeira vez.' }),
  recurring: AudienceCountSchema.nullable().meta({ description: 'Visitantes recorrentes.' }),
  kids: AudienceCountSchema.nullable().meta({
    description: 'Crianças: já fazem parte do total presencial (não se somam de novo).',
  }),
  online: AudienceCountSchema.nullable().meta({ description: 'Público online, separado do presencial.' }),
  method: AudienceMethodSchema,
  note: z.string().max(280).meta({ description: 'Observação operacional, sem dado pessoal.' }),
};

export const ServiceAudienceSchema = z
  .object({
    ...AudienceInputShape,
    recordedBy: z.string().meta({ description: 'Nome de quem registrou.' }),
    recordedAt: z.number().int().meta({ description: 'Quando foi registrado (epoch ms).' }),
    updatedBy: z.string().nullable().meta({ description: 'Quem editou por último (`null` sem edição).' }),
    updatedAt: z.number().int().nullable(),
  })
  .meta({
    id: 'ServiceAudience',
    description:
      'Público do culto: contagens agregadas, sem identificar ninguém. Não é o check-in de quem serviu (`attendance`).',
  });

export const ScheduleEventSchema = z
  .object({
    id: IdSchema,
    seriesId: IdSchema,
    title: z.string().min(1).max(120),
    date: IsoDateSchema,
    time: TimeSchema,
    recurrence: z
      .string()
      .nullable()
      .meta({ description: 'Texto da recorrência ou `null` para evento único.' }),
    rule: RecurrenceRuleSchema.nullable().meta({
      description: 'A regra da repetição, a mesma em todas as datas da série; `null` no evento único.',
    }),
    color: HexColorSchema.nullable().meta({
      description: 'A cor do evento no calendário (`#RRGGBB`); `null` = a automática da série.',
    }),
    ministryIds: z.array(IdSchema),
    need: z
      .record(IdSchema, z.number().int().min(0).max(50))
      .meta({ description: 'Quantidade por função quando difere da padrão (0 = fora do evento).' }),
    slots: z.record(IdSchema, z.array(IdSchema)).meta({ description: 'roleId → memberIds escalados.' }),
    generated: z.boolean(),
    published: z.boolean(),
    edited: z.boolean(),
    responses: z
      .record(IdSchema, ScheduleResponseSchema)
      .meta({ description: 'memberId → confirmação ou recusa. Escalado sem resposta = pendente.' }),
    attendance: z.record(IdSchema, AttendanceSchema).meta({ description: 'memberId → check-in do dia.' }),
    nudgedAt: z
      .number()
      .int()
      .nullable()
      .meta({ description: 'Última cobrança aos pendentes (epoch ms), ou `null`.' }),
    recorded: z.boolean().meta({
      description: 'Histórico registrado: a marcação de quem serviu foi concluída (Escalas › Histórico).',
    }),
    notices: z.record(IdSchema, ScheduleNoticeSchema).meta({
      description:
        'memberId → a última mudança na escala publicada que a pessoa precisa saber. O Membro recebe só a própria.',
    }),
    swaps: z.array(SwapRequestSchema).meta({
      description:
        'Pedidos de troca, abertos e encerrados. O Membro recebe só os que fez ou recebeu; a equipe, todos.',
    }),
    audience: ServiceAudienceSchema.nullable().meta({
      description: 'Público do culto; `null` = não registrado (não é zero). O Membro recebe sempre `null`.',
    }),
  })
  .meta({ id: 'ScheduleEvent' });

/** O evento com a versão usada na concorrência otimista (`If-Match`). */
export const EventResourceSchema = ScheduleEventSchema.extend({
  version: z.number().int().min(0),
}).meta({ id: 'EventResource' });

export const LogEntrySchema = z
  .object({
    id: IdSchema,
    eventId: IdSchema,
    who: z.string(),
    text: z.string(),
    at: z.number().int().meta({ description: 'Momento em milissegundos (epoch).' }),
  })
  .meta({ id: 'LogEntry', description: 'Linha do histórico de alterações de um evento.' });

export const ReminderChannelSchema = z
  .enum(['email', 'whatsapp'])
  .meta({ id: 'ReminderChannel', description: 'Canal dos lembretes e cobranças.' });

export const JourneyStageSchema = z
  .object({ key: z.string().min(1).max(40), label: z.string().trim().min(1).max(30), active: z.boolean() })
  .meta({
    id: 'JourneyStage',
    description: 'Etapa da jornada de integração (administrativa; não mede fé nem compromisso).',
  });

export const SettingsSchema = z
  .object({
    maxPerMonth: z.number().int().min(1).max(31),
    minGapDays: z.number().int().min(0).max(60),
    allowUnset: z.boolean(),
    notifyPublished: z.boolean(),
    notifyConflicts: z.boolean(),
    notifyReminders: z.boolean().meta({ description: 'Lembrete antes da escala.' }),
    reminderDaysBefore: z
      .number()
      .int()
      .min(1)
      .max(7)
      .meta({ description: 'Antecedência do lembrete, em dias.' }),
    reminderChannels: z.array(ReminderChannelSchema).max(2),
    nudgePending: z.boolean().meta({ description: 'Cobra quem não confirmou presença.' }),
    nudgeAfterDays: z
      .number()
      .int()
      .min(1)
      .max(7)
      .meta({ description: 'Dias após a publicação (ou a cobrança anterior).' }),
    nudgeMax: z.number().int().min(1).max(5).meta({ description: 'Cobranças automáticas, no máximo.' }),
    leaderDigest: z.boolean().meta({ description: 'Resumo semanal para os líderes, toda segunda.' }),
    visitorJourney: z.array(JourneyStageSchema).min(3).max(12).meta({
      description: 'Etapas da jornada de integração dos visitantes, na ordem do quadro.',
    }),
    visitorRetentionMonths: z.number().int().min(3).max(24).meta({
      description: 'Meses sem visita nem contato até a ficha de visitante ser anonimizada.',
    }),
  })
  .meta({ id: 'Settings' });

export const AccessStatusSchema = z.enum(['active', 'invited', 'expired', 'notInvited', 'noEmail']).meta({
  id: 'AccessStatus',
  description:
    'Situação do acesso: ativo (tem senha), convite enviado, convite expirado, não convidado ou sem e-mail.',
});

export const MemberAccessSchema = z
  .object({
    memberId: IdSchema,
    role: AppRoleSchema,
    status: AccessStatusSchema,
    invitedAt: z.iso.datetime().nullable(),
    inviteExpiresAt: z.iso.datetime().nullable(),
    invitedBy: z.string().nullable().meta({ description: 'Nome de quem enviou o último convite.' }),
    activatedAt: z.iso.datetime().nullable().meta({ description: 'Quando a senha foi criada.' }),
    lastLoginAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'MemberAccess', description: 'O acesso de um membro à aplicação.' });

export const SessionSchema = z
  .object({
    role: AppRoleSchema,
    memberId: IdSchema,
    platformAdmin: z.boolean().optional().meta({
      description:
        'Administrador da plataforma: gerencia as organizações e entra em qualquer uma como Administrador.',
    }),
  })
  .meta({ id: 'Session', description: 'Quem está usando a aplicação.' });

export const OrgStatusSchema = z
  .enum(['active', 'suspended'])
  .meta({ id: 'OrgStatus', description: 'Suspensa bloqueia o login, mas mantém os dados.' });

export const OrgSlugSchema = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9-]{1,38})[a-z0-9]$/, 'Use só letras minúsculas sem acento, números e hífen.')
  .refine((s) => !s.includes('--'), 'Sem hífen duplo.')
  .meta({ description: 'Endereço de acesso (`livegospel.app/{slug}`).' });

export const OrganizationSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1).max(80),
    slug: OrgSlugSchema,
    city: z.string().max(80),
    uf: z.union([z.literal(''), z.string().regex(/^[A-Z]{2}$/)]),
    address: AddressSchema.meta({
      description: 'Endereço da igreja; `city` e `uf` repetem os dele quando informado.',
    }),
    location: MemberLocationSchema.nullable().meta({
      description: 'Coordenada do endereço (o pin do mapa da Membresia); `null` até o servidor localizar.',
    }),
    color: HexColorSchema,
    status: OrgStatusSchema,
    createdAt: IsoDateSchema,
  })
  .meta({ id: 'Organization', description: 'Uma igreja que usa o Live Gospel.' });

export const RegistrationDataSchema = z
  .object({
    name: z.string().min(1).max(120),
    birthDate: IsoDateSchema.nullable(),
    sex: SexSchema.nullable(),
    civilStatus: CivilStatusSchema.nullable(),
    profession: z.string().max(80),
    phone: PhoneDigitsSchema,
    email: z.union([z.literal(''), z.email().max(254)]),
    address: AddressSchema,
    churchStatus: ChurchStatusSchema.nullable(),
    memberSince: IsoDateSchema.nullable(),
    joinedVia: JoinedViaSchema.nullable(),
    baptismDate: IsoDateSchema.nullable(),
    originChurch: z.string().max(120),
    ministryIds: z.array(IdSchema).max(50).meta({ description: 'Ministérios em que gostaria de servir.' }),
    notes: z.string().max(1000),
  })
  .meta({ id: 'RegistrationData', description: 'O que a pessoa preenche no formulário público.' });

export const MergeFieldSchema = z
  .enum([
    'photo',
    'name',
    'birthDate',
    'sex',
    'civilStatus',
    'profession',
    'phone',
    'email',
    'address',
    'churchStatus',
    'memberSince',
    'joinedVia',
    'baptismDate',
    'originChurch',
  ])
  .meta({ id: 'MergeField' });

export const RegistrationSchema = z
  .object({
    id: IdSchema,
    data: RegistrationDataSchema,
    status: z.enum(['pending', 'approved', 'rejected']),
    submittedAt: z.number().int(),
    decidedAt: z.number().int().nullable(),
    decidedBy: z.string(),
    reason: z.string().max(300),
    memberId: IdSchema.nullable().meta({ description: 'O membro criado ou vinculado na aprovação.' }),
    linked: z.boolean().meta({ description: 'Vinculada a um cadastro que já existia.' }),
    mergedFields: z
      .array(MergeFieldSchema)
      .meta({ description: 'Dados do cadastro existente substituídos pelos da solicitação.' }),
    photo: z
      .string()
      .max(PHOTO_URL_MAX)
      .nullable()
      .meta({
        description:
          'Endereço da foto enviada com o cadastro (opcional), `/api/v1/registrations/{id}/photo/{token}`, ' +
          'servida sem login como a do membro. Só enquanto pendente: na decisão vira a foto do membro ou é apagada.',
      }),
  })
  .meta({ id: 'Registration', description: 'Cadastro enviado pelo link público.' });

export const RegistrationLinkSchema = z
  .object({
    token: z.string().min(1).max(64),
    active: z.boolean().meta({ description: 'Pausado: quem abre o link vê "Cadastros encerrados".' }),
    createdAt: z.number().int(),
  })
  .meta({ id: 'RegistrationLink', description: 'O link público de cadastro da organização.' });

export const PrivacyRequestKindSchema = z.enum(['revoke', 'deletion', 'other']).meta({
  id: 'PrivacyRequestKind',
  description: 'Revogar o consentimento, excluir a conta (anonimizar) ou outro pedido sobre os dados.',
});

export const PrivacyRequestSchema = z
  .object({
    id: IdSchema,
    memberId: IdSchema,
    kind: PrivacyRequestKindSchema,
    note: z.string().max(1000),
    status: z.enum(['open', 'done', 'denied']),
    createdAt: z.number().int(),
    dueDate: IsoDateSchema.meta({ description: 'Prazo da resposta: 15 dias depois do envio.' }),
    resolvedAt: z.number().int().nullable(),
    resolvedBy: z.string(),
    response: z.string().max(1000),
    anonymized: z.boolean().meta({ description: 'Exclusão atendida com o cadastro anonimizado.' }),
  })
  .meta({ id: 'PrivacyRequest', description: 'Pedido do titular dos dados (LGPD, art. 18).' });

export const CommunicationPrefsSchema = z
  .object({ email: z.boolean(), whatsapp: z.boolean(), push: z.boolean() })
  .meta({
    id: 'CommunicationPrefs',
    description:
      'Canais de lembretes e cobranças que a pessoa aceita receber (os avisos de acesso sempre saem).',
  });

export const AccountPrivacySchema = z
  .object({
    termsVersion: z.string().max(20).nullable().meta({
      description: 'Versão dos Termos e da Política aceita; `null` = nunca aceitou.',
    }),
    termsAcceptedAt: z.iso.datetime().nullable(),
    communications: CommunicationPrefsSchema,
  })
  .meta({ id: 'AccountPrivacy', description: 'O aceite dos documentos e as comunicações da conta.' });

// ===== Visitantes =====

export const AgeRangeSchema = z.enum(['18-29', '30-44', '45-59', '60+', 'unknown']).meta({ id: 'AgeRange' });
export const VisitorSourceSchema = z
  .enum(['invite', 'social', 'passing', 'event', 'online', 'other'])
  .meta({ id: 'VisitorSource', description: 'Como a pessoa conheceu a igreja.' });
export const FollowUpKindSchema = z
  .enum(['whatsapp', 'call', 'visit', 'message', 'other'])
  .meta({ id: 'FollowUpKind' });
export const FollowUpStatusSchema = z.enum(['pending', 'doing', 'done']).meta({ id: 'FollowUpStatus' });
export const ContactOutcomeSchema = z
  .enum(['reached', 'noAnswer', 'declined'])
  .meta({ id: 'ContactOutcome', description: '`declined` = não quer mais contato (arquiva a ficha).' });

const VisitorNoteSchema = z.string().max(140);

export const VisitorVisitSchema = z
  .object({ date: IsoDateSchema, eventId: IdSchema.nullable() })
  .meta({ id: 'VisitorVisit' });

export const VisitorFollowUpSchema = z
  .object({
    id: IdSchema,
    kind: FollowUpKindSchema,
    ownerId: IdSchema.nullable(),
    due: IsoDateSchema,
    status: FollowUpStatusSchema,
    note: VisitorNoteSchema,
    doneAt: z.number().int().nullable(),
    doneBy: z.string(),
  })
  .meta({ id: 'VisitorFollowUp', description: 'Acompanhamento: tarefa com responsável e prazo.' });

export const VisitorContactSchema = z
  .object({
    id: IdSchema,
    at: z.number().int(),
    kind: FollowUpKindSchema,
    by: z.string(),
    outcome: ContactOutcomeSchema,
  })
  .meta({ id: 'VisitorContact' });

export const VisitorSchema = z
  .object({
    id: IdSchema,
    name: z.string().max(120),
    phone: PhoneDigitsSchema,
    email: z.union([z.literal(''), z.email().max(254)]),
    ageRange: AgeRangeSchema,
    district: z.string().max(80),
    source: VisitorSourceSchema,
    invitedById: IdSchema.nullable(),
    visits: z.array(VisitorVisitSchema).min(1).max(500),
    stage: z.string().min(1).max(40),
    ownerId: IdSchema.nullable(),
    wantsContact: z.boolean(),
    wantsMembership: z.boolean(),
    consent: z.object({ at: z.number().int(), version: z.string().max(20), by: z.enum(['self', 'staff']) }),
    origin: z.enum(['staff', 'link']),
    registrationId: IdSchema.nullable().meta({
      description: 'Cadastro de membro aberto pela ficha; o membro vem da aprovação dele.',
    }),
    note: VisitorNoteSchema,
    followUps: z.array(VisitorFollowUpSchema),
    contacts: z.array(VisitorContactSchema),
    archivedAt: z.number().int().nullable(),
    anonymized: z.boolean(),
    createdAt: z.number().int(),
    createdBy: z.string(),
  })
  .meta({
    id: 'Visitor',
    description:
      'Ficha de visitante, com consentimento. Para quem não gerencia visitantes, vem sem identificação (nome, contato e observações vazios).',
  });

export const VisitorLinkSchema = z
  .object({ token: z.string().min(1).max(64), active: z.boolean(), createdAt: z.number().int() })
  .meta({ id: 'VisitorLink', description: 'O link público da ficha de visitante.' });

/** Tudo o que a interface precisa para montar a store, já recortado pelo perfil. */
export const StateSchema = z
  .object({
    ministries: z.array(MinistrySchema),
    members: z.array(MemberSchema),
    events: z.array(EventResourceSchema),
    log: z.array(LogEntrySchema),
    settings: SettingsSchema,
    readNotifications: z.array(z.string()),
    onboardingHidden: z.boolean(),
    registrations: z.array(RegistrationSchema).meta({
      description: 'Cadastros pelo link público — só para o Administrador (os demais recebem `[]`).',
    }),
    registrationLink: RegistrationLinkSchema.nullable().meta({
      description: 'O link público de cadastro — só para o Administrador.',
    }),
    privacyRequests: z.array(PrivacyRequestSchema).meta({
      description: 'Pedidos de privacidade: todos para o Administrador; os próprios para os demais.',
    }),
    visitors: z.array(VisitorSchema).meta({
      description:
        'Fichas de visitantes: inteiras para quem gerencia; sem identificação para os outros líderes; `[]` para o Membro.',
    }),
    visitorLink: VisitorLinkSchema.nullable().meta({
      description: 'O link da ficha — só para quem gerencia.',
    }),
  })
  .meta({ id: 'State' });

export type MemberDto = z.infer<typeof MemberSchema>;
export type MinistryDto = z.infer<typeof MinistrySchema>;
export type RoleDto = z.infer<typeof RoleSchema>;
export type EventResource = z.infer<typeof EventResourceSchema>;
export type LogEntryDto = z.infer<typeof LogEntrySchema>;
export type SettingsDto = z.infer<typeof SettingsSchema>;
export type StateDto = z.infer<typeof StateSchema>;
export type OrganizationDto = z.infer<typeof OrganizationSchema>;
export type RegistrationDto = z.infer<typeof RegistrationSchema>;
